import {
  Badge,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { PlusIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { Statute } from '../../types/Statute.type';
import StatuteAutocomplete from '../StatuteAutocomplete/StatuteAutocomplete';

export interface ChargeEnhancement {
  id: string;
  enhancements: Statute[];
  assignedChargeIds: number[];
}

interface EnhancementsDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (caseEnhancements: Statute[], chargeEnhancements: ChargeEnhancement[]) => void;
  initialCaseEnhancements: Statute[];
  initialChargeEnhancements: ChargeEnhancement[];
  caseCharges: Statute[];
  countyId: number | null;
  disabled?: boolean;
}

function newChargeEnhancement(): ChargeEnhancement {
  return { id: crypto.randomUUID(), enhancements: [], assignedChargeIds: [] };
}

export default function EnhancementsDialog({
  open,
  onClose,
  onApply,
  initialCaseEnhancements,
  initialChargeEnhancements,
  caseCharges,
  countyId,
  disabled = false,
}: EnhancementsDialogProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [caseEnhancements, setCaseEnhancements] = useState<Statute[]>(initialCaseEnhancements);
  const [chargeEnhancements, setChargeEnhancements] = useState<ChargeEnhancement[]>(
    initialChargeEnhancements.length > 0 ? initialChargeEnhancements : [newChargeEnhancement()],
  );
  const [applyErrors, setApplyErrors] = useState<string[]>([]);
  const [prevOpen, setPrevOpen] = useState(open);

  const caseEnhancementCount = caseEnhancements.length;

  const totalChargeDyads = () => {
    const chargeEnhancementCount = chargeEnhancements.filter((e) => e.enhancements.length > 0);
    return chargeEnhancementCount.reduce((sum, group) => sum + group.assignedChargeIds.length, 0);
  };

  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setCaseEnhancements(initialCaseEnhancements);
      setChargeEnhancements(
        initialChargeEnhancements.length > 0 ? initialChargeEnhancements : [newChargeEnhancement()],
      );
      setApplyErrors([]);
      setActiveTab(0);
    }
  }

  const handleAddChargeEnhancement = () => {
    setChargeEnhancements((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        enhancements: [],
        assignedChargeIds: [],
      },
    ]);
  };

  const handleRemoveChargeEnhancement = (id: string) => {
    setChargeEnhancements((prev) => prev.filter((e) => e.id !== id));
  };

  const handleEnhancementsChange = (id: string, statutes: Statute[]) => {
    setChargeEnhancements((prev) => prev.map((e) => (e.id === id ? { ...e, enhancements: statutes } : e)));
  };

  const handleToggleChargeAssignment = (enhancementId: string, chargeId: number) => {
    setChargeEnhancements((prev) =>
      prev.map((e) => {
        if (e.id !== enhancementId) return e;
        const isAssigned = e.assignedChargeIds.includes(chargeId);
        return {
          ...e,
          assignedChargeIds: isAssigned
            ? e.assignedChargeIds.filter((cid) => cid !== chargeId)
            : [...e.assignedChargeIds, chargeId],
        };
      }),
    );
  };

  const handleApply = () => {
    const errors: string[] = [];

    if (activeTab === 1) {
      const availableEnhancements = chargeEnhancements.filter((e) => e.enhancements.length > 0);

      // Each charge enhancement must be assigned to at least one charge
      const unassignedCharges = availableEnhancements.filter((e) => e.assignedChargeIds.length === 0);
      if (unassignedCharges.length > 0) {
        errors.push('Each charge enhancement must be assigned to at least one charge.');
      }

      // No duplicate enhancement groups (same set of statute IDs)
      const chargesGroup: number[][] = [];
      for (const e of availableEnhancements) {
        const sorted = [...e.enhancements.map((s) => s.id)].sort((a, b) => a - b);
        if (chargesGroup.some((g) => g.length === sorted.length && g.every((v, i) => v === sorted[i]))) {
          errors.push('Duplicate charge enhancements are not allowed.');
          break;
        }
        chargesGroup.push(sorted);
      }
    }

    setApplyErrors(errors);
    if (errors.length > 0) return;

    onApply(caseEnhancements, chargeEnhancements);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>Enhancements</DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => {
              setActiveTab(v as number);
              setApplyErrors([]);
            }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Case
                  {caseEnhancementCount > 0 && (
                    <Badge
                      badgeContent={caseEnhancementCount}
                      color='primary'
                      sx={{
                        '& .MuiBadge-badge': {
                          position: 'relative',
                          transform: 'none',
                          fontSize: '11px',
                          borderRadius: '10px',
                          paddingX: '10px',
                          backgroundColor: 'primary.main',
                          color: '#fff',
                        },
                      }}
                    />
                  )}
                </Box>
              }
              sx={{ textTransform: 'none' }}
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Charge
                  {totalChargeDyads() > 0 && (
                    <Badge
                      badgeContent={totalChargeDyads()}
                      color='primary'
                      sx={{
                        '& .MuiBadge-badge': {
                          position: 'relative',
                          transform: 'none',
                          fontSize: '11px',
                          borderRadius: '10px',
                          paddingX: '10px',
                          backgroundColor: 'primary.main',
                          color: '#fff',
                        },
                      }}
                    />
                  )}
                </Box>
              }
              sx={{ textTransform: 'none' }}
            />
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <Box>
            <StatuteAutocomplete
              countyId={countyId}
              value={caseEnhancements}
              onChange={setCaseEnhancements}
              disabled={disabled || caseCharges.length === 0}
              isCaseEnhancement={true}
            />
            {caseCharges.length === 0 && (
              <Typography color='error' variant='body2' sx={{ fontWeight: 500, marginTop: 1 }}>
                Add a charge first before adding enhancements.
              </Typography>
            )}
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {chargeEnhancements.map((chargeEnh) => (
              <Box
                key={chargeEnh.id}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    Charge Enhancement
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => handleRemoveChargeEnhancement(chargeEnh.id)}
                    disabled={disabled}
                  >
                    <XIcon size={16} />
                  </IconButton>
                </Box>
                <StatuteAutocomplete
                  countyId={countyId}
                  value={chargeEnh.enhancements}
                  onChange={(v) => handleEnhancementsChange(chargeEnh.id, v)}
                  disabled={disabled || caseCharges.length === 0}
                  isChargeEnhancement={true}
                  singleSelect={true}
                />
                {chargeEnh.enhancements.length > 0 && (
                  <Box>
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: 500,
                        marginTop: 2,
                        marginBottom: 1.5,
                      }}
                    >
                      Assign to Charge(s)
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {caseCharges.length === 0 ? (
                        <Typography
                          variant='caption'
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          No case charges to assign to.
                        </Typography>
                      ) : (
                        caseCharges.map((charge) => {
                          const isAssigned = chargeEnh.assignedChargeIds.includes(charge.id);
                          return (
                            <Chip
                              key={charge.id}
                              label={charge.display_name}
                              variant={isAssigned ? 'filled' : 'outlined'}
                              color={isAssigned ? 'primary' : 'default'}
                              onClick={
                                disabled ? undefined : () => handleToggleChargeAssignment(chargeEnh.id, charge.id)
                              }
                              sx={{ cursor: disabled ? 'default' : 'pointer' }}
                            />
                          );
                        })
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            ))}
            {caseCharges.length === 0 && (
              <Typography color='error' variant='body2' sx={{ fontWeight: 500 }}>
                Add a charge first before adding charge-level enhancements.
              </Typography>
            )}
          </Box>
        )}

        {applyErrors.length > 0 && (
          <Box sx={{ marginTop: 2 }}>
            {applyErrors.map((err) => (
              <Typography color='error' variant='body2' sx={{ fontWeight: 500 }} key={err}>
                {err}
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: activeTab === 1 ? 'space-between' : 'flex-end', px: 3, pb: 2 }}>
        {activeTab === 1 && (
          <Button
            variant='outlined'
            disableElevation
            startIcon={<PlusIcon size={16} />}
            onClick={handleAddChargeEnhancement}
            disabled={disabled}
            sx={{ fontWeight: 600 }}
          >
            Add Enhancement
          </Button>
        )}
        <Button
          disableElevation
          variant='contained'
          color='accentBlue'
          sx={{ color: '#fff', paddingX: '48px', fontWeight: 600 }}
          onClick={handleApply}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
